import { Controller } from "@hotwired/stimulus"

// Caption text synced to the sibling player. The full transcript stays in
// visually-hidden source spans (one per segment, each carrying data-start-second)
// so screen readers always get the whole transcript; only the active line or
// lines render visually.
//
// Two shapes use this one engine:
//   - the 3-line reading window (previous · current · next) in purchases/media
//   - the single-line always-on caption band over case avatar clips
// prev/next are OPTIONAL — a template may render `current` alone. Stimulus THROWS
// on a missing target getter, so every access goes through its has…Target guard.
//
// A segment may also carry data-end-second, in which case the line clears once
// playback passes it — that is what lets the band empty during trailing silence
// instead of freezing the last sentence under a silent avatar. Omit it and a line
// simply holds until the next one starts (the reading window's behaviour).
//
// Usage: put data-controller="live-captions" on a wrapper that contains BOTH the
// player ([data-media-player-target="media"]) and the rendered caption partial.
export default class extends Controller {
  static targets = ["segment", "prev", "current", "next"]

  connect() {
    this.activeIndex = null
    this.media = this.element.querySelector('[data-media-player-target="media"]') ||
                 this.element.querySelector("video, audio")
    this.starts = this.segmentTargets.map((el) => {
      const start = Number.parseFloat(el.dataset.startSecond)
      return Number.isFinite(start) ? start : 0
    })
    this.ends = this.segmentTargets.map((el) => {
      const end = Number.parseFloat(el.dataset.endSecond)
      return Number.isFinite(end) ? end : null
    })

    this.boundUpdate = this.update.bind(this)
    if (this.media) {
      this.media.addEventListener("timeupdate", this.boundUpdate)
      this.media.addEventListener("seeked", this.boundUpdate)
      this.media.addEventListener("loadedmetadata", this.boundUpdate)
    }

    this.update()
  }

  disconnect() {
    if (!this.media) return

    this.media.removeEventListener("timeupdate", this.boundUpdate)
    this.media.removeEventListener("seeked", this.boundUpdate)
    this.media.removeEventListener("loadedmetadata", this.boundUpdate)
  }

  update() {
    const time = this.media && Number.isFinite(this.media.currentTime) ? this.media.currentTime : 0

    let active = 0
    for (let i = this.starts.length - 1; i >= 0; i--) {
      if (time >= this.starts[i]) {
        active = i
        break
      }
    }

    // Past its own end with the next line not yet due: the gap belongs to no segment, so nothing
    // is shown. Only applies where end times were rendered — without them `ends[active]` is null
    // and a line holds until its successor starts, exactly as before.
    const end = this.ends[active]
    const blank = end !== null && end !== undefined && time > end
    const shown = blank ? null : active

    if (shown === this.activeIndex) return
    this.activeIndex = shown

    this.setLine("prev", shown === null ? -1 : shown - 1)
    this.setLine("current", shown === null ? -1 : shown)
    this.setLine("next", shown === null ? -1 : shown + 1)
  }

  setLine(name, index) {
    if (!this[`has${name[0].toUpperCase()}${name.slice(1)}Target`]) return

    const target = this[`${name}Target`]
    const source = index >= 0 && index < this.segmentTargets.length ? this.segmentTargets[index] : null
    target.textContent = source ? source.textContent.trim() : ""
  }
}
